<script setup lang="ts">
import { computed } from "vue";
import { AwSkeleton } from "@asap/design";
import { achievementStatus, deltaStatus, formatDelta, formatEuro, statusColorName } from "./theme";
import type { KpiTile, TrajectoryHeader } from "./types";

const SKELETON_TILE_COUNT = 3;

const props = withDefaults(
  defineProps<{
    title?: string;
    trajectory?: TrajectoryHeader;
    kpis?: KpiTile[];
    loading?: boolean;
  }>(),
  { loading: false, kpis: undefined, title: "Trajectoire", trajectory: undefined }
);

const frameTitle = computed(() => props.trajectory?.title ?? props.title);

const ui = {
  summary: "flex flex-col gap-2",
  summaryWithData: "rounded-lg border border-secondary-200 bg-secondary-25 p-3",
  seriesLabel: "text-sm text-secondary-800",
  valueRow: "flex flex-wrap items-baseline gap-2",
  currentValue: "text-2xl font-semibold leading-none text-secondary-900",
  delta: "text-xs font-medium",
  comparison: "text-xs text-secondary-700",
  kpiGrid: "grid grid-cols-3 gap-3",
  skeletonChart: "h-[120px] w-full rounded-lg",
};
</script>

<template>
  <ConsultantKpiCard :title="frameTitle">
    <div :class="[ui.summary, trajectory && ui.summaryWithData]">
      <AwSkeleton v-if="loading" :class="ui.skeletonChart" />
      <template v-else-if="trajectory">
        <span :class="ui.seriesLabel">{{ trajectory.seriesLabel }}</span>

        <div :class="ui.valueRow">
          <span :class="ui.currentValue">{{ formatEuro(trajectory.currentValue) }}</span>
          <span :class="[ui.delta, `text-${statusColorName(deltaStatus(trajectory.delta))}-600`]">
            {{ formatDelta(trajectory.delta) }}
          </span>
          <span :class="ui.comparison">{{ trajectory.comparison }}</span>
        </div>

        <ConsultantKpiProgressBar
          :percent="trajectory.achievementPercent"
          :status="achievementStatus(trajectory.achievementPercent)"
          :leading-label="`${trajectory.achievementPercent}%`"
          :trailing-label="trajectory.targetLabel"
          tinted-track
        />
      </template>

      <slot v-if="!loading"></slot>
    </div>

    <div :class="ui.kpiGrid">
      <template v-if="loading">
        <ConsultantKpiTile v-for="index in SKELETON_TILE_COUNT" :key="`trajectory-skeleton-${index}`" loading />
      </template>
      <template v-else>
        <ConsultantKpiTile v-for="kpi in kpis" :key="kpi.label" v-bind="kpi" />
      </template>
    </div>
  </ConsultantKpiCard>
</template>
